import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Save, Loader2, MessageSquare, Clock, Shield } from 'lucide-react';
import { RuleConditionBuilder } from './RuleConditionBuilder';
import {
  SocialAutoReplyRule,
  CreateAutoReplyRuleRequest,
  AutoReplyTriggerType,
  AutoReplyTriggerConfig,
  ActiveSchedule,
  SocialPlatform,
} from '@/types/social';

interface AutoReplyRuleBuilderProps {
  businessId: string;
  editingRule?: SocialAutoReplyRule | null;
  onSave: (request: CreateAutoReplyRuleRequest) => Promise<void>;
  onCancel: () => void;
}

export const AutoReplyRuleBuilder: React.FC<AutoReplyRuleBuilderProps> = ({
  businessId,
  editingRule,
  onSave,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [triggerType, setTriggerType] = useState<AutoReplyTriggerType>('keyword');
  const [triggerConfig, setTriggerConfig] = useState<AutoReplyTriggerConfig>({});
  const [platforms, setPlatforms] = useState<SocialPlatform[]>(['instagram', 'facebook']);
  const [responseType, setResponseType] = useState<'text' | 'template'>('text');
  const [responseContent, setResponseContent] = useState('');
  const [responseDelaySeconds, setResponseDelaySeconds] = useState(0);
  const [maxTriggersPerDay, setMaxTriggersPerDay] = useState(3);
  const [activeSchedule, setActiveSchedule] = useState<ActiveSchedule>({});

  // Initialize form with editing rule data
  useEffect(() => {
    if (editingRule) {
      setName(editingRule.name);
      setTriggerType(editingRule.trigger_type);
      setTriggerConfig(editingRule.trigger_config || {});
      setPlatforms(editingRule.platforms);
      setResponseType(editingRule.response_type);
      setResponseContent(editingRule.response_content);
      setResponseDelaySeconds(editingRule.response_delay_seconds);
      setMaxTriggersPerDay(editingRule.max_triggers_per_contact_per_day);
      setActiveSchedule(editingRule.active_schedule || {});
    }
  }, [editingRule]);

  const handleSubmit = async () => {
    if (!name.trim() || !responseContent.trim()) {
      return;
    }

    // Validate keyword trigger has keywords
    if (triggerType === 'keyword' && (!triggerConfig.keywords || triggerConfig.keywords.length === 0)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        business_id: businessId,
        name: name.trim(),
        trigger_type: triggerType,
        trigger_config: triggerConfig,
        platforms,
        response_type: responseType,
        response_content: responseContent.trim(),
        response_delay_seconds: responseDelaySeconds,
        max_triggers_per_contact_per_day: maxTriggersPerDay,
        active_schedule: activeSchedule,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid =
    name.trim() &&
    responseContent.trim() &&
    platforms.length > 0 &&
    (triggerType !== 'keyword' || (triggerConfig.keywords && triggerConfig.keywords.length > 0));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {editingRule ? 'Edit Rule' : 'Create Auto-Reply Rule'}
          </h2>
          <p className="text-muted-foreground">
            {editingRule
              ? 'Update your auto-reply rule settings'
              : 'Set up an automatic response to incoming messages'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Rule name */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Rule Details</CardTitle>
              <CardDescription>Give your rule a descriptive name</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="name">Rule Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Welcome Message, Business Hours Reply"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Trigger conditions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Trigger Conditions</CardTitle>
              <CardDescription>Define when this auto-reply should be sent</CardDescription>
            </CardHeader>
            <CardContent>
              <RuleConditionBuilder
                triggerType={triggerType}
                triggerConfig={triggerConfig}
                platforms={platforms}
                activeSchedule={activeSchedule}
                onTriggerTypeChange={setTriggerType}
                onTriggerConfigChange={setTriggerConfig}
                onPlatformsChange={setPlatforms}
                onActiveScheduleChange={setActiveSchedule}
              />
            </CardContent>
          </Card>

          {/* Response content */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Response Message
              </CardTitle>
              <CardDescription>The message that will be sent automatically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="response">Message Content</Label>
                <Textarea
                  id="response"
                  placeholder="Hi! Thanks for reaching out. We'll get back to you as soon as possible."
                  value={responseContent}
                  onChange={(e) => setResponseContent(e.target.value)}
                  rows={4}
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Keep it concise and friendly</span>
                  <span>{responseContent.length} characters</span>
                </div>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2 text-sm">Available Variables</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <code className="bg-background px-2 py-1 rounded">{'{first_name}'}</code>
                  <span className="text-muted-foreground">Contact's first name</span>
                  <code className="bg-background px-2 py-1 rounded">{'{username}'}</code>
                  <span className="text-muted-foreground">Contact's username</span>
                  <code className="bg-background px-2 py-1 rounded">{'{platform}'}</code>
                  <span className="text-muted-foreground">Platform name</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Settings sidebar */}
        <div className="space-y-6">
          {/* Response timing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Response Timing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>Delay before sending</Label>
                <div className="flex items-center gap-4">
                  <Slider
                    value={[responseDelaySeconds]}
                    onValueChange={([value]) => setResponseDelaySeconds(value)}
                    max={60}
                    step={5}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-12">
                    {responseDelaySeconds}s
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  A small delay makes the response feel more natural
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Rate limiting */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Rate Limiting
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>Max replies per contact per day</Label>
                <Select
                  value={maxTriggersPerDay.toString()}
                  onValueChange={(value) => setMaxTriggersPerDay(parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 reply</SelectItem>
                    <SelectItem value="2">2 replies</SelectItem>
                    <SelectItem value="3">3 replies</SelectItem>
                    <SelectItem value="5">5 replies</SelectItem>
                    <SelectItem value="10">10 replies</SelectItem>
                    <SelectItem value="0">Unlimited</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Prevents the same contact from receiving too many auto-replies
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm">
                    A
                  </div>
                  <div className="flex-1">
                    <div className="bg-background rounded-lg p-3 text-sm">
                      {responseContent || 'Your message preview will appear here...'}
                    </div>
                    {responseDelaySeconds > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Sent after {responseDelaySeconds}s delay
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingRule ? 'Save Changes' : 'Create Rule'}
                </>
              )}
            </Button>
            <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoReplyRuleBuilder;
